import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { ApiError, api, apiErrorCode, type ConnectorVerifyResult } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { connectorKeys, lowerFirst } from "@/lib/connectors";
import { ConnectionForm } from "@/pages/container-tabs/integrations/connectors/ConnectionPanel";
import { ChecksList, ConnectHeading } from "@/pages/container-tabs/integrations/connectors/parts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CheckCircle2, Clock } from "lucide-react";
import { useEffect, useState } from "react";

// /connect/connector#<token>: the one-time link Boo sends one person to fill
// in a connector's connection. No sign-in and no dashboard around it: the
// connector's name, its fields, Save and test, and the test's checks.
export function ConnectorLinkPage() {
  // Links minted before the token moved to the fragment carry it in the path.
  const { token: fromPath } = useParams({ strict: false }) as { token?: string };
  // The token is the credential: read once, then kept in memory only, out of
  // the address bar, history and analytics. The fragment never reaches a server.
  const [token, setToken] = useState(() => tokenFromHash() ?? fromPath);
  const [done, setDone] = useState<ConnectorVerifyResult | null>(null);
  const [expiredNow, setExpiredNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => {
    const clear = () =>
      navigate({ to: "/connect/connector/{-$token}", params: { token: undefined }, hash: "", replace: true });
    if (fromPath || window.location.hash) void clear();
    // Another link, or the same one again, opened in this tab: ask the backend
    // afresh, since the cached answer predates any save through it.
    const onHash = () => {
      const next = tokenFromHash();
      if (!next) return;
      void queryClient.resetQueries({ queryKey: connectorKeys.link(next) });
      setToken(next);
      setDone(null);
      setExpiredNow(false);
      setBusy(false);
      void clear();
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [fromPath, navigate, queryClient]);
  useNoReferrer();
  const query = useQuery({
    queryKey: connectorKeys.link(token ?? ""),
    queryFn: () => api.connectorLink.get(token!),
    enabled: !!token,
    refetchOnWindowFocus: false,
  });
  const view = query.data;
  const minutes = useMinutesLeft(view?.expiresAt);
  useDocumentTitle(view ? `Connect ${view.name}` : "Connect");

  // The backend decides expiry; the countdown is the browser's clock.
  const expired = expiredNow || (query.isError && apiErrorCode(query.error) === "link_expired");

  return (
    <div className="ph-no-capture flex min-h-screen flex-col items-center bg-background px-4 py-10 sm:py-16">
      <div className="flex w-full max-w-xl flex-col gap-6">
        {!token ? (
          <Notice title="Open the link again" body="This page forgets the link once it has opened. Open it from Boo’s message again." />
        ) : query.isLoading ? (
          <LoadingIndicator />
        ) : done ? (
          <Done name={view?.name ?? "it"} booName={view?.booName ?? "Boo"} result={done} />
        ) : expired ? (
          <Notice title="This link has expired" body="Ask Boo for a new one." />
        ) : query.isError ? (
          query.error instanceof ApiError && query.error.status === 404 ? (
            <Notice title="This link doesn’t work" body="Check that you copied all of it, or ask Boo for a new one." />
          ) : (
            <InlineError error={query.error} />
          )
        ) : !view ? null : (
          <>
            <ConnectHeading name={view.name} booName={view.booName} />
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="size-4 shrink-0" />
              {minutes === null || minutes > 0
                ? `This link works once and expires in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`
                : "This link is about to expire. If saving fails, ask Boo for a new one."}
            </p>
            {view.verify?.description && (
              <p className="text-sm text-muted-foreground text-pretty">
                The connection test {lowerFirst(view.verify.description)}
              </p>
            )}
            {busy && (
              <p className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                Someone is saving through this link right now. Try again in a moment.
              </p>
            )}
            <ConnectionForm
              setup
              hideStatus
              connector={{ slug: "", name: view.name, variables: view.variables, connection: null, live: true }}
              containerId=""
              canEdit
              canManage={false}
              save={async (values) => {
                setBusy(false);
                try {
                  return await api.connectorLink.saveVariables(token!, values);
                } catch (err) {
                  if (apiErrorCode(err) === "link_expired") setExpiredNow(true);
                  throw err;
                }
              }}
              onChanged={() => {}}
              onSaved={setDone}
              onSaveError={(err) => {
                if (apiErrorCode(err) !== "link_busy") return false;
                setBusy(true);
                return true;
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function Done({ name, booName, result }: { name: string; booName: string; result: ConnectorVerifyResult }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2 py-4 text-center">
        <CheckCircle2 className="size-8 text-emerald-600" />
        <h1 className="text-lg font-semibold">Connected. You can close this page.</h1>
        <p className="text-sm text-muted-foreground text-pretty">
          {booName} can now use {name}.{result.account ? ` Connected to ${result.account}.` : ""} Secret values are stored
          encrypted and never shown again.
        </p>
      </div>
      {result.checks.length > 0 && <ChecksList checks={result.checks} />}
    </div>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border p-8 text-center">
      <p className="text-base font-semibold">{title}</p>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function tokenFromHash(): string | undefined {
  try {
    return decodeURIComponent(window.location.hash.slice(1)) || undefined;
  } catch {
    return undefined;
  }
}

// Keeps the page's address out of the Referer header of anything it loads.
function useNoReferrer() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

// Minutes until expiresAt (to the nearest, at least 1 while any time is
// left), ticking every 15 s; null until known.
function useMinutesLeft(expiresAt: string | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt) - now;
  return ms <= 0 ? 0 : Math.max(1, Math.round(ms / 60_000));
}
